import { DecimalPipe } from '@angular/common';
import { Component, Input, OnInit, inject, DestroyRef, model } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { MessageService } from 'src/app/core/services/broadcast/message-service';
import { DataService } from 'src/app/core/services/data/data.service';
import { InvoiceService } from 'src/app/core/services/data/invoice.service';
import { AddedAdjustments } from 'src/app/shared/entities/AddedAdjustments';
import { AdjustmentsCompletedGB } from 'src/app/shared/entities/AdjustmentsCompletedGB';
import { InvoiceAdjustmentReasons } from 'src/app/shared/entities/InvoiceAdjustmentReasons';
import { InvoiceAdjustments } from 'src/app/shared/entities/InvoiceAdjustments';
import { TaxListMatchingInvoiceDate } from 'src/app/shared/entities/TaxListMatchingInvoiceDate';
import { IInvoiceAdjustmentAmount, IGBInvoiceAdjustmentDisplayData } from 'src/app/shared/interfaces';
import { environment } from 'src/environments/environment';
import { AddInvoiceAdjustment } from 'src/app/shared/entities/AddInvoiceAdjustment';
import { AddInvoiceAdjustmentTax } from 'src/app/shared/entities/AddInvoiceAdjustmentTax';
import { InvoiceAdjustment } from 'src/app/shared/entities/InvoiceAdjustment';
import { AdjustTaxes } from 'src/app/shared/entities/AdjustTaxes';
import { PostMigrateAdjustment } from 'src/app/shared/entities/PostMigrateAdjustment';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { QuickSearchResults } from 'src/app/shared/entities/QuickSearchResults';
import { QueryMessageService } from 'src/app/core/services/broadcast/MessageDispatchingService';

@Component({
  selector: 'app-gb-invoice-adjustments',
  standalone: false,
  templateUrl: './gb-invoice-adjustments.component.html',
  styleUrl: './gb-invoice-adjustments.component.scss',
})
export class GbInvoiceAdjustmentsComponent implements OnInit {
  @Input()
  set invoiceNumber(value: string) {
    this.currentInvoiceNumber = value;
    const invoiceType = this.currentInvoiceNumber.substring(0, 2);
  }
  currentInvoiceNumber: string = '';
  allinvoiceBillingLines: InvoiceAdjustments[] = [];
  invoiceBillingLines: InvoiceAdjustments[] = [];
  completedAdjustmentsGB: AdjustmentsCompletedGB[] = [];
  addedAdjustments: AddedAdjustments[] = [];
  invoiceAdjustmentReasons: InvoiceAdjustmentReasons[] = [];
  matchingTaxListForInvoice: TaxListMatchingInvoiceDate[] = [];
  isCurrentInvoiceLineTaxable: boolean | undefined = undefined;

  netAmountMinimumLimit: number = 0;
  netAmountMaximumLimit: number = 0;
  grossAmountMinimumLimit: number = 0;
  grossAmountMaximumLimit: number = 0;
  currentInvoiceTaxRate: number = 0;
  isDev: boolean = false;
  invoiceBeingAdjustedDetails: InvoiceAdjustments = new InvoiceAdjustments();
  adjustedNetRevenue: number;
  showZeroBalanceInvoiceLines: boolean = false;
  showWaitMessage: boolean = false;

  netAmountReadOnly: boolean = true;
  grossAmountReadOnly: boolean = true;

  gbInvoiceLineForm = new FormGroup({
    invoiceLineItem: new FormControl('', []),
    currentDate: new FormControl(this.getCurrentDate(), [Validators.required]),
    netAmount: new FormControl('', [
      Validators.required,
      Validators.min(this.netAmountMinimumLimit),
      Validators.max(this.netAmountMaximumLimit),
    ]),
    grossAmount: new FormControl('', [
      Validators.required,
      Validators.min(this.grossAmountMinimumLimit),
      Validators.max(this.grossAmountMaximumLimit),
    ]),
    adjustmentReason: new FormControl('', [Validators.required]),
    adjustmentComment: new FormControl('', [Validators.required, Validators.maxLength(512)]),
  });

  // SERVICES
  #_invoiceService = inject(InvoiceService);
  #_dataService = inject(DataService);
  #_appGeneralState = inject(AppGeneralStateService);
  #_decimalPipe = inject(DecimalPipe);
  #_snackBar = inject(MatSnackBar);
  #_messageService = inject(MessageService);
  #_messageDialogService = inject(MessageDialogService);
  #_queryMessagegService = inject(QueryMessageService);

  constructor() {
    this.isDev = false; //this.#_appGeneralState.isEnvironmentDev;
  }

  ngOnInit(): void {
    this.getTaxListThatMatchInvoiceNumber(this.currentInvoiceNumber);
    this.getInvoiceAdjustmentReasonsForLotNumberFromDB(this.#_appGeneralState.currentLotNumber);
    this.getAdjustmentsCompletedForGB(this.currentInvoiceNumber);
  }

  onChangeShowBillingLines(): void {
    this.showZeroBalanceInvoiceLines = !this.showZeroBalanceInvoiceLines;

    if (!this.showZeroBalanceInvoiceLines) {
      this.invoiceBillingLines = this.allinvoiceBillingLines;
    } else {
      this.invoiceBillingLines = [];
      let ii: number = 0;
      for (ii = 0; ii < this.allinvoiceBillingLines.length; ii++) {
        const lineItemTotal: number =
          this.allinvoiceBillingLines[ii].totalAmount + this.allinvoiceBillingLines[ii].adjustment;
        if (lineItemTotal != 0) {
          this.invoiceBillingLines.push(this.allinvoiceBillingLines[ii]);
        }
      }
    }

    this.resetInvoiceBillingLineData();
  }

  onSubmitNewInvoiceAdjustment(): void {
    if (this.showWaitMessage == true) return;

    this.showWaitMessage = true;

    let invoiceAdjustmentDetails: AddInvoiceAdjustment = new AddInvoiceAdjustment();
    let invoiceAdjustmentTaxDetails: AddInvoiceAdjustmentTax[] = [];
    let primaryKeyForAdjustmentLine: string = this.gbInvoiceLineForm.get('invoiceLineItem').value;

    this.invoiceBeingAdjustedDetails = this.getInvoiceAdjustmentDetailsForLineItem(primaryKeyForAdjustmentLine);

    let grossAmount: number = +this.gbInvoiceLineForm.get('grossAmount').value.replace(',', '');

    this.adjustedNetRevenue = +this.gbInvoiceLineForm.get('netAmount').value.replace(',', '');

    invoiceAdjustmentDetails.AdjustmentAmount = this.adjustedNetRevenue;

    invoiceAdjustmentDetails.AdjustmentReasonId = +this.gbInvoiceLineForm.get('adjustmentReason').value;
    invoiceAdjustmentDetails.BillingDetailId = 0;
    invoiceAdjustmentDetails.Comments = this.gbInvoiceLineForm.get('adjustmentComment').value;
    invoiceAdjustmentDetails.GenerateBillingLineItemId = this.invoiceBeingAdjustedDetails.generateBillingLineItemId;
    invoiceAdjustmentDetails.InvoiceNumber = this.currentInvoiceNumber;
    invoiceAdjustmentDetails.IsLocked = false;
    invoiceAdjustmentDetails.LotNumber = this.#_appGeneralState.currentLotNumber;
    invoiceAdjustmentDetails.RevenueCode = ''; //this.getRevenueCodeFromRevenueName(this.invoiceBeingAdjustedDetails.description);
    invoiceAdjustmentDetails.UserName = this.#_appGeneralState.userName;
    invoiceAdjustmentDetails.ManualInvoiceId = this.invoiceBeingAdjustedDetails.manualInvoiceId;

    //todo refactor in separate method
    let ii: number = 0;
    let today = new Date();
    if (this.isCurrentInvoiceLineTaxable) {
      invoiceAdjustmentTaxDetails.push({
        AdjustmentPostingTaxId: 0,
        AdjustmentPostingId: 0,
        BillingDetailId: 0,
        TaxType: 'Total',
        AdjustedTax: grossAmount - this.adjustedNetRevenue,
        AdjustedTaxMoney: grossAmount - this.adjustedNetRevenue,
        DateEntered: today,
      });
    }

    this.postIUAdjustment(invoiceAdjustmentDetails, invoiceAdjustmentTaxDetails, this.isCurrentInvoiceLineTaxable);
  }

  getInvoiceAdjustmentDetailsForLineItem(primaryKeyValue: string): InvoiceAdjustments {
    let ii: number = 0;
    let adjustmentDetails: InvoiceAdjustments = new InvoiceAdjustments();
    for (ii = 0; ii < this.invoiceBillingLines.length; ii++) {
      if (primaryKeyValue === this.invoiceBillingLines[ii].primaryKey) {
        adjustmentDetails = this.invoiceBillingLines[ii];
      }
    }
    return adjustmentDetails;
  }

  // This method gets called when the user selects an invoice line in the invoice line drop down list
  onInvoiceLineSelectionChange(): void {
    // Get the selected invoice line data
    try {
      const primarykey: string = this.gbInvoiceLineForm.get('invoiceLineItem').value;

      // Clear any results from a different line
      this.gbInvoiceLineForm.patchValue({
        netAmount: '',
        grossAmount: '',
        adjustmentReason: '',
        adjustmentComment: '',
      });

      // If the primary key is blank/empty (designated by ''), it means the user chose the top dropdown item, which is the 'Select Invoice Line'
      if (primarykey == '') {
        // this path is blank/empty
        //This will reset the form elements
        this.netAmountReadOnly = true;
        this.grossAmountReadOnly = true;
        this.isCurrentInvoiceLineTaxable = undefined;
        this.resetInvoiceBillingLineData();
      } else {
        // The user chose an invoice line.
        // Get this specific line
        const invoiceLine: InvoiceAdjustments = this.invoiceBillingLines.find(
          (line) => line.generateBillingLineItemId == primarykey,
        );

        // Set the taxable status of this invoiceline
        // If the invoiceLine.tax > 0, then it has tax and is taxable.

        this.isCurrentInvoiceLineTaxable = invoiceLine.tax > 0 ? true : false;

        // Prep all of the values needed for the selected invoice line
        const preppedInvoiceLineValues: IGBInvoiceAdjustmentDisplayData = this.prepInvoiceLineDisplay(invoiceLine);

        //this is dev only
        this.currentInvoiceTaxRate = preppedInvoiceLineValues.taxRate;

        // Set form values of prepped Invoice Billing Line
        this.setFormValuesForInvoiceBillingLine(preppedInvoiceLineValues);

        this.setNetGrossValidationLimits(preppedInvoiceLineValues);
      }
    } catch (err) {
      this.netAmountReadOnly = true;
      this.grossAmountReadOnly = true;
    }
  }

  prepInvoiceLineDisplay(invoiceLine: InvoiceAdjustments): IGBInvoiceAdjustmentDisplayData {
    let invoiceLineValues: IGBInvoiceAdjustmentDisplayData = {} as IGBInvoiceAdjustmentDisplayData;

    invoiceLineValues.grossAmount = invoiceLine.totalAmount + invoiceLine.adjustment;
    invoiceLineValues.taxRate = this.getTaxRateForIinvoiceLine(invoiceLine);

    invoiceLineValues.netAmount = invoiceLineValues.grossAmount / (1 + invoiceLineValues.taxRate);

    invoiceLineValues.taxAmount = this.getTaxAmountForInvoiceLine(
      invoiceLineValues.grossAmount,
      invoiceLineValues.taxRate,
    );

    return invoiceLineValues;
  }

  getTaxRateForIinvoiceLine(invoiceLine: InvoiceAdjustments): number {
    let taxRate: number;
    let ii: number;

    if (false == this.isCurrentInvoiceLineTaxable) {
      taxRate = 0;
    } else {
      taxRate = 0;
      for (ii = 0; ii < this.matchingTaxListForInvoice.length; ii++) {
        taxRate = taxRate + this.matchingTaxListForInvoice[ii].taxPercentage / 100;
      }
    }

    return taxRate;
  }

  setNetGrossValidationLimits(invoiceLineData: IGBInvoiceAdjustmentDisplayData): void {
    this.netAmountMinimumLimit =
      invoiceLineData.netAmount > 0 ? invoiceLineData.netAmount * -1 : invoiceLineData.netAmount;

    this.netAmountMaximumLimit =
      invoiceLineData.netAmount < 0 ? invoiceLineData.netAmount * -1 : invoiceLineData.netAmount;

    this.grossAmountMinimumLimit =
      invoiceLineData.grossAmount > 0 ? invoiceLineData.grossAmount * -1 : invoiceLineData.grossAmount;

    this.grossAmountMaximumLimit =
      invoiceLineData.grossAmount < 0 ? invoiceLineData.grossAmount * -1 : invoiceLineData.grossAmount;

    this.netAmountMinimumLimit = +this.#_decimalPipe
      .transform(this.netAmountMinimumLimit, '1.2-2')
      ?.toString()
      .replace(',', '');
    this.netAmountMaximumLimit = +this.#_decimalPipe
      .transform(this.netAmountMaximumLimit, '1.2-2')
      ?.toString()
      .replace(',', '');
    this.grossAmountMinimumLimit = +this.#_decimalPipe
      .transform(this.grossAmountMinimumLimit, '1.2-2')
      ?.toString()
      .replace(',', '');
    this.grossAmountMaximumLimit = +this.#_decimalPipe
      .transform(this.grossAmountMaximumLimit, '1.2-2')
      ?.toString()
      .replace(',', '');

    this.gbInvoiceLineForm
      .get('netAmount')
      .setValidators([
        Validators.min(+this.#_decimalPipe.transform(this.netAmountMinimumLimit, '1.2-2')?.toString().replace(',', '')),
        Validators.max(+this.#_decimalPipe.transform(this.netAmountMaximumLimit, '1.2-2')?.toString().replace(',', '')),
        Validators.required,
      ]);
    this.gbInvoiceLineForm.get('netAmount').updateValueAndValidity();

    this.gbInvoiceLineForm
      .get('grossAmount')
      .setValidators([
        Validators.min(
          +this.#_decimalPipe.transform(this.grossAmountMinimumLimit, '1.2-2')?.toString().replace(',', ''),
        ),
        Validators.max(
          +this.#_decimalPipe.transform(this.grossAmountMaximumLimit, '1.2-2')?.toString().replace(',', ''),
        ),
        Validators.required,
      ]);

    this.gbInvoiceLineForm.get('grossAmount').updateValueAndValidity();
  }

  getTaxAmountForInvoiceLine(grossAmount: number, taxRate: number): number {
    let taxAmount: number;

    if (false == this.isCurrentInvoiceLineTaxable) {
      taxAmount = 0;
    } else {
      taxAmount = grossAmount / (1 + taxRate);
    }

    return taxAmount;
  }

  setFormValuesForInvoiceBillingLine(preppedInvoiceLine: IGBInvoiceAdjustmentDisplayData): void {
    try {
      const netTestZeroBalance: number = +this.#_decimalPipe
        .transform(preppedInvoiceLine.grossAmount * -1, '1.2-2')
        ?.toString()
        .replace(',', '');

      if (netTestZeroBalance == 0) {
        this.netAmountReadOnly = true;
        this.grossAmountReadOnly = true;
      } else {
        this.netAmountReadOnly = false;
        this.grossAmountReadOnly = false;

        this.gbInvoiceLineForm.patchValue({
          grossAmount: this.#_decimalPipe.transform(preppedInvoiceLine.grossAmount * -1, '1.2-2')?.toString(),
          netAmount: this.#_decimalPipe.transform(preppedInvoiceLine.netAmount * -1, '1.2-2')?.toString(),
        });
      }
    } catch (err) {
      this.netAmountReadOnly = true;
      this.grossAmountReadOnly = true;
    }
  }

  //Get adjustment totals for a given invoice billing line
  getTotalsForInvoiceBillingLine(billingLineId: any): IInvoiceAdjustmentAmount {
    var adjustmentTotals: IInvoiceAdjustmentAmount = {} as IInvoiceAdjustmentAmount;
    var subtotal: number = 0;
    var tax: number = 0;
    var grandTotal: number = 0;
    var ii: number = 0;

    try {
      for (ii = 0; ii < this.completedAdjustmentsGB.length; ii++) {
        //NOTE: the generatebillinglineitemid could hold the line detail for a GB or MI
        if (this.completedAdjustmentsGB[ii].generateBillingLineItemId == billingLineId) {
          subtotal += this.completedAdjustmentsGB[ii].adjustmentAmount;
          tax += this.completedAdjustmentsGB[ii].taxAmount;
          grandTotal += this.completedAdjustmentsGB[ii].grossAdjustmentValue;
        }
      }

      adjustmentTotals.subtotal = subtotal;
      adjustmentTotals.tax = tax;
      adjustmentTotals.total = grandTotal;
    } catch (err) {}

    return adjustmentTotals;
  }

  resetInvoiceBillingLineData(): void {
    this.gbInvoiceLineForm.patchValue({
      netAmount: '',
      grossAmount: '',
      invoiceLineItem: '',
      adjustmentReason: '',
      adjustmentComment: '',
    });
  }

  onAdjustmentCommentLostFocus(): void {
    let testEmptyString: string = this.gbInvoiceLineForm.get('adjustmentComment').value;
    let removeSpaces: string = testEmptyString.trim();

    if (removeSpaces.length == 0) {
      this.gbInvoiceLineForm.patchValue({
        adjustmentComment: '',
      });
    }

    this.gbInvoiceLineForm.get('adjustmentComment').updateValueAndValidity();
  }

  onNetAmountLostFocus(): void {
    let netAmount: number = +this.gbInvoiceLineForm.get('netAmount').value.replace(',', '');
    let netAmountAsString = this.gbInvoiceLineForm.get('netAmount').value.replace(',', '');
    let grossAmount: number = 0;

    if (netAmountAsString == '') {
      this.gbInvoiceLineForm.patchValue({
        grossAmount: '',
      });

      this.gbInvoiceLineForm.patchValue({
        netAmount: '',
      });
    } else {
      if (netAmount > 0) {
        netAmount = netAmount * -1;
      }

      if (this.isCurrentInvoiceLineTaxable) {
        grossAmount = netAmount * (1 + this.currentInvoiceTaxRate);
      } else {
        grossAmount = netAmount;
      }

      this.gbInvoiceLineForm.patchValue({
        grossAmount: this.#_decimalPipe.transform(grossAmount, '1.2-2')?.toString(),
      });

      this.gbInvoiceLineForm.patchValue({
        netAmount: this.#_decimalPipe.transform(netAmount, '1.2-2')?.toString(),
      });
    }
  }

  onGrossAmountLostFocus(): void {
    let grossAmount: number = +this.gbInvoiceLineForm.get('grossAmount').value.replace(',', '');
    let grossAmountAsString = this.gbInvoiceLineForm.get('grossAmount').value.replace(',', '');
    let netAmount: number = 0;

    if (grossAmountAsString == '') {
      this.gbInvoiceLineForm.patchValue({
        grossAmount: '',
      });

      this.gbInvoiceLineForm.patchValue({
        netAmount: '',
      });
    } else {
      if (grossAmount > 0) {
        grossAmount = grossAmount * -1;
      }

      if (this.isCurrentInvoiceLineTaxable) {
        netAmount = grossAmount / (1 + this.currentInvoiceTaxRate);
      } else {
        netAmount = grossAmount;
      }

      this.gbInvoiceLineForm.patchValue({
        netAmount: this.#_decimalPipe.transform(netAmount, '1.2-2')?.toString(),
      });

      this.gbInvoiceLineForm.patchValue({
        grossAmount: this.#_decimalPipe.transform(grossAmount, '1.2-2')?.toString(),
      });
    }
  }

  // DATA METHODS

  postIUAdjustment(
    invoiceAdjustmentData: AddInvoiceAdjustment,
    invoiceAdjustmentTax: AddInvoiceAdjustmentTax[],
    isTaxable: boolean,
  ): void {
    let newAdjustmentPostingId: number;
    let adjustmentData: InvoiceAdjustment = new InvoiceAdjustment();
    let adjustTaxes: AdjustTaxes = new AdjustTaxes();
    let migration: PostMigrateAdjustment = new PostMigrateAdjustment();
    let statusResult: boolean;

    adjustTaxes.adjustmentPostingId = 0; //This is just for initialization.  The actual new adjustmentPostingId is created and set in the API data methods
    adjustTaxes.invoiceNumber = invoiceAdjustmentData.InvoiceNumber;
    migration.invoiceNumber = invoiceAdjustmentData.InvoiceNumber;

    //Assign different adjustment objects to adjustmentData object
    adjustmentData.adjustmentPosting = invoiceAdjustmentData;
    adjustmentData.adjustmentPostingTax = invoiceAdjustmentTax;
    adjustmentData.adjustTaxes = adjustTaxes;
    adjustmentData.migrationAdjustment = migration;

    this.#_invoiceService.postInvoiceAdjustmentAllData(adjustmentData).subscribe(
      (postingAdjustmentResultStatus) => {
        statusResult = postingAdjustmentResultStatus;
      },
      (err: any) => {
        this.showWaitMessage = false;
        // let errorLogEntry: dbErrorEntry = new dbErrorEntry();

        // addExceptionMessage only logs to the console - <app-error-messages> is not placed in any
        // template, so the snackbar is the only feedback the user actually sees.
        this.#_messageDialogService.snackBarTopCenterTimed(
          'An error occurred and the adjustment failed to be recorded.',
          5000,
        );

        this.#_messageService.addExceptionMessage(
          'An error occurred while submitting this adjustment. Please review the adjustment data and resubmit. If the error continues to occur, please contact APN Support. Error Id #: ' +
            err.error,
        );
      },
      () => {
        this.showWaitMessage = false;

        this.getInvoiceAdjustmentReasonsForLotNumberFromDB(this.#_appGeneralState.currentLotNumber);
        this.getAdjustmentsCompletedForGB(this.currentInvoiceNumber);
        this.getInvoiceDetailBalancesForInvoiceNumber(this.currentInvoiceNumber);
        this.resetInvoiceBillingLineData();

        this.gbInvoiceLineForm.get('netAmount').setErrors(null);
        this.gbInvoiceLineForm.get('grossAmount').setErrors(null);
        this.gbInvoiceLineForm.get('adjustmentReason').setErrors(null);
        this.gbInvoiceLineForm.get('adjustmentComment').setErrors(null);
        this.gbInvoiceLineForm.updateValueAndValidity();

        this.#_messageDialogService.snackBarTopCenterTimed('This adjustment have been successfully submitted.', 5000);
      },
    );
  }

  getInvoiceDetailBalancesForInvoiceNumber(invoiceNumber: string): void {
    let quickSearchResults: QuickSearchResults;

    this.#_invoiceService
      .getInvoiceDetailByInvoiceNumber(invoiceNumber, this.#_appGeneralState.currentLotNumber)
      .subscribe({
        error: (err: any) => {},
        next: (results) => (quickSearchResults = results),
        complete: () => {
          this.#_queryMessagegService.currentInvoiceBalance = quickSearchResults.currentBalance;
        },
      });
  }

  getTaxListThatMatchInvoiceNumber(invoiceNumber: string): void {
    this.#_invoiceService.getTaxListThatMatchInvoiceDate(invoiceNumber).subscribe(
      (x) => {
        this.matchingTaxListForInvoice = x;
      },
      (err: any) => {},
      () => {
        let ii: number = 0;

        for (ii = 0; ii < this.matchingTaxListForInvoice.length; ii++) {
          let description: string = this.matchingTaxListForInvoice[ii].taxName;
          // remove old from tax type description
          description = description.replace(' - OLD', '');
          this.matchingTaxListForInvoice[ii].taxName = description;
        }
      },
    );
  }

  private getInvoiceAdjustmentReasonsForLotNumberFromDB(lotNumber: string): void {
    this.#_invoiceService.getAdjustmentReasonsForLotNumber(lotNumber).subscribe(
      (invoiceAdjustmentReasons) => {
        this.invoiceAdjustmentReasons = invoiceAdjustmentReasons;
      },
      (err: any) => {},
      () => {},
    );
  }

  getAdjustmentsCompletedForGB(invoiceNumber: string): void {
    this.#_invoiceService.getAdjustmentsCompleted_GB(invoiceNumber).subscribe(
      (adjustmentListResults) => {
        this.completedAdjustmentsGB = adjustmentListResults;
      },
      (err: any) => {
        // let errorLogEntry: dbErrorEntry = new dbErrorEntry();
        // errorLogEntry.Location = 'Err: invoice-adjustments.ts::getAdjustmentsCompletedForGB().';
        // errorLogEntry.errorMessage = JSON.stringify(err);
        // this.apnUtilityService.logErrorToDB(errorLogEntry);
      },
      () => {
        this.getInvoiceBilingLinesForInvoiceNumber_GB(this.currentInvoiceNumber);
      },
    );
  }

  getInvoiceBilingLinesForInvoiceNumber_GB(invoiceNumber: string) {
    this.#_invoiceService.getAdjustmentsForInvoiceNumber_GB(invoiceNumber).subscribe(
      (invoiceBillingLines) => {
        this.invoiceBillingLines = invoiceBillingLines;
      },
      (err: any) => {
        // let errorLogEntry: dbErrorEntry = new dbErrorEntry();
        // errorLogEntry.Location = 'Err: invoice-adjustments.ts::getInvoiceAdjustmentsForInvoiceNumber_GB().';
        // errorLogEntry.errorMessage = JSON.stringify(err);
        // this.apnUtilityService.logErrorToDB(errorLogEntry);
      },
      () => {
        //copy generateBillingLineItemID to primaryKey
        let ii: number = 0;
        for (ii = 0; ii < this.invoiceBillingLines.length; ii++) {
          this.invoiceBillingLines[ii].gbOriginalBillingDetailId = this.invoiceBillingLines[ii].billingDetailID;
        }

        ii = 0;
        for (ii = 0; ii < this.invoiceBillingLines.length; ii++) {
          this.invoiceBillingLines[ii].primaryKey = this.invoiceBillingLines[ii].generateBillingLineItemId;

          //Add any adjustmnets to invoiceBillingLines array
          if (this.invoiceBillingLines[ii].adjustment < 0) {
            this.addedAdjustments.push({
              description: this.invoiceBillingLines[ii].customerName,
              adjustmentAmount: this.invoiceBillingLines[ii].adjustment,
            });
          }

          const adjustmentTotals: IInvoiceAdjustmentAmount = this.getTotalsForInvoiceBillingLine(
            this.invoiceBillingLines[ii].generateBillingLineItemId,
          );

          this.invoiceBillingLines[ii].calcedAdjustmentAmount = adjustmentTotals.total;
        }
        this.allinvoiceBillingLines = this.invoiceBillingLines;
      },
    );
  }

  // UTILITY METHODS

  getCurrentDate(): any {
    var today = new Date();
    var returnDate = '';

    var dd = today.getDate();
    var mm = today.getMonth() + 1;
    var yyyy = today.getFullYear();

    returnDate += `${mm}/${dd}/${yyyy}`;

    return returnDate;
  }
}
